import { THEME_COLOR_NAMES, type ThemeColorName } from '@examdojo/styles';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import {
  NEUTRAL_EDGE_SHADES,
  THEME_COLOR_SHADES,
  type NeutralEdgeShade,
  type ThemeColorShade,
  type ThemeColorWithShade,
} from '../theme/theme.model';

export interface ParsedThemeColor {
  name: ThemeColorName;
  shade: ThemeColorShade | NeutralEdgeShade;
}

const VALID_NEUTRAL_SHADES: readonly number[] = [...THEME_COLOR_SHADES, ...NEUTRAL_EDGE_SHADES];

/**
 * Parse a theme color string into its name and shade components.
 * Bare names default to shade 500. Neutral additionally accepts edge shades 0 and 1000.
 *
 * @example
 * parseThemeColor('sky')         // { name: 'sky', shade: 500 }
 * parseThemeColor('sky-300')     // { name: 'sky', shade: 300 }
 * parseThemeColor('neutral-0')   // { name: 'neutral', shade: 0 }
 */
export function parseThemeColor(color: ThemeColorWithShade): ParsedThemeColor {
  const dashIndex = color.lastIndexOf('-');
  if (dashIndex > 0) {
    const maybeName = color.slice(0, dashIndex);
    const maybeShade = Number(color.slice(dashIndex + 1));
    if (THEME_COLOR_NAMES.includes(maybeName as ThemeColorName)) {
      const validShades = maybeName === 'neutral' ? VALID_NEUTRAL_SHADES : THEME_COLOR_SHADES;
      if ((validShades as readonly number[]).includes(maybeShade)) {
        return { name: maybeName as ThemeColorName, shade: maybeShade as ThemeColorShade };
      }
    }
  }
  return { name: color as ThemeColorName, shade: 500 };
}

/**
 * Resolve a theme color (with optional shade) to a CSS color string.
 */
export function resolveColor(
  themeService: JsxgraphThemeService,
  color: ThemeColorWithShade | undefined,
): string | undefined {
  if (!color) {
    return undefined;
  }
  return themeService.resolveColorToken(color);
}
